// GitHub sign-in (RESTYLE §7, IMPL_ADDENDUM §2b), shown only when GET /v1/config enables auth.
//   <Account />       nav control. Signed out: a plain <a href="/v1/auth/github/login?return=<path + search>">
//                     "Sign in with GitHub" (GitHub glyph; "Sign in" under 1200px, the glyph alone under 1000px).
//                     Signed in: 20px square avatar + login, opening a raised popover ("Signed in as …" · Sign out,
//                     POST /v1/auth/logout then ["me"] is invalidated). Esc or a click outside closes it. Under
//                     720px the nav has no room and the control moves to the footer.
//                     The session cookie is HttpOnly: JS never reads it; /v1/auth/me is re-checked on focus and every 60 s.
//   <AccountLine />   the footer's phone line: "sign in with GitHub" or "signed in as <login> · sign out"
// Nothing visible renders until the session is known, so a signed-in viewer never sees "Sign in" flash; until then an
// invisible "Sign in" holds its width, so the nav's right cluster does not jump when /v1/config answers.
import { useEffect, useId, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Icon } from "@/components/ui";
import { loginHref, useConfig, useLogout, useMe, useUpdateDisplayName } from "@/hooks";
import { isApiError } from "@/lib/api";
import type { User } from "@/types";

function useAccount() {
  const { data: config } = useConfig();
  const enabled = !!config?.auth.enabled;
  const me = useMe({ enabled });
  const { pathname, search } = useLocation();
  const logout = useLogout();
  return {
    ready: enabled && me.isSuccess,
    // config still loading, or auth on and the session not known yet
    pending: !config || (enabled && me.isPending),
    user: me.data?.authenticated ? me.data.user : null,
    href: config ? loginHref(config, `${pathname}${search}`) : "",
    logout,
  };
}

function logoutError(err: unknown): string {
  return isApiError(err) ? `${err.status} from ${err.path}` : "no response";
}

/** GitHub serves any size; ask for 2× the 20px box instead of the 460px default. */
function avatarSrc(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const u = new URL(url, window.location.href);
    if (u.hostname === "avatars.githubusercontent.com") u.searchParams.set("s", "40");
    return u.href;
  } catch {
    return url;
  }
}

function Avatar({ user }: { user: User }) {
  const [failed, setFailed] = useState(false);
  const src = avatarSrc(user.avatar_url);
  if (!src || failed) {
    return (
      <span className="grid size-5 shrink-0 place-items-center rounded-token border border-rule-field text-micro uppercase text-ink-mute" aria-hidden="true">
        {user.github_login.slice(0, 1)}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      width={20}
      height={20}
      className="size-5 shrink-0 rounded-token bg-sunk object-cover"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

export function Account() {
  const { ready, pending, user, href, logout } = useAccount();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const signIn = useRef<HTMLAnchorElement>(null);
  const refocus = useRef(false);
  const popId = useId();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      // a modal sheet on top takes Esc for itself
      if (e.key !== "Escape" || document.querySelector("dialog[open]")) return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener("pointerdown", onDown);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  // signed out (here or in another tab): close the popover; after our own sign-out, focus "Sign in"
  useEffect(() => {
    if (user) return;
    setOpen(false);
    if (refocus.current) {
      refocus.current = false;
      signIn.current?.focus();
    }
  }, [user]);

  if (!ready) {
    if (!pending) return null;
    return (
      <span className="nav-link invisible inline-flex items-center gap-2 max-[720px]:hidden" aria-hidden="true">
        <Icon name="github" size={13} />
        <span className="max-[1000px]:sr-only">
          Sign in<span className="max-[1200px]:hidden"> with GitHub</span>
        </span>
      </span>
    );
  }

  if (!user) {
    return (
      <a ref={signIn} className="nav-link inline-flex items-center gap-2 max-[720px]:hidden" href={href}>
        <Icon name="github" size={13} />
        <span className="max-[1000px]:sr-only">
          Sign in<span className="max-[1200px]:hidden"> with GitHub</span>
        </span>
      </a>
    );
  }

  const signOut = () => {
    refocus.current = true;
    logout.mutate(undefined, {
      onError: () => {
        refocus.current = false;
      },
    });
  };

  return (
    <div
      ref={wrap}
      className="relative max-[720px]:hidden"
      onBlur={(e) => {
        // focus left the control (Tab away, or a sheet took it): close
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        ref={trigger}
        type="button"
        className="inline-flex min-h-10 items-center gap-2 px-1 text-data text-ink-mute transition-colors hover:text-ink aria-expanded:text-ink"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        aria-label={`Account: ${user.github_login}`}
        onClick={() => setOpen((o) => !o)}
      >
        <Avatar user={user} />
        <span className="max-w-[16ch] truncate max-[1000px]:hidden" translate="no">
          {user.github_login}
        </span>
      </button>
      {open && (
        <div
          id={popId}
          role="dialog"
          aria-label="Account"
          tabIndex={-1}
          className="absolute right-0 top-[calc(100%+6px)] z-10 w-64 border border-rule-strong bg-raised p-4 shadow-pop transition-[opacity,translate] duration-160 ease-out starting:translate-y-1 starting:opacity-0"
        >
          <p className="lens-eyebrow">Signed in as</p>
          <p className="mt-2 flex min-w-0 items-center gap-2.5 text-data text-ink">
            <Avatar user={user} />
            <span className="min-w-0 truncate" translate="no">
              {user.github_login}
            </span>
          </p>
          <DisplayNameEditor user={user} />
          <div className="mt-4 border-t border-rule-cell pt-3">
            <button
              type="button"
              className="btn btn--secondary btn--sm w-full"
              aria-disabled={logout.isPending || undefined}
              onClick={() => logout.isPending || signOut()}
            >
              {logout.isPending ? "Signing out…" : "Sign out"}
            </button>
            {logout.isError && <p className="mt-2 text-meta text-ink-mute">Couldn’t sign out · {logoutError(logout.error)}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function DisplayNameEditor({ user }: { user: User }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user.name ?? "");
  const update = useUpdateDisplayName();
  useEffect(() => {
    if (!editing) setValue(user.name ?? "");
  }, [user.name, editing]);
  if (!editing) {
    return (
      <p className="mt-1.5 flex items-baseline gap-2 font-sans text-ui">
        <span className="min-w-0 truncate text-ink-soft">{user.name || <span className="text-ink-mute">no display name</span>}</span>
        <button type="button" className="lnk text-meta" onClick={() => setEditing(true)}>
          edit
        </button>
      </p>
    );
  }
  const save = () => {
    update.mutate(value.trim(), {
      onSuccess: () => setEditing(false),
    });
  };
  return (
    <div className="mt-2">
      <label className="lens-eyebrow block">Display name</label>
      <input
        autoFocus
        type="text"
        maxLength={64}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") {
            e.stopPropagation();
            setEditing(false);
          }
        }}
        placeholder="shown on your submissions when opt-in is checked"
        className="mt-1 w-full border border-rule-field bg-paper px-2 py-1.5 text-ui text-ink focus:border-ink focus:outline-none"
      />
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          aria-disabled={update.isPending || undefined}
          onClick={save}
        >
          {update.isPending ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => setEditing(false)}
        >
          Cancel
        </button>
      </div>
      {update.isError && <p className="mt-1 text-meta text-ink-mute">couldn’t save · try again</p>}
    </div>
  );
}

export function AccountLine() {
  const { ready, user, href, logout } = useAccount();
  if (!ready) return null;
  if (!user) {
    return (
      <span className="min-[720px]:hidden">
        <a href={href}>sign in with GitHub</a>
      </span>
    );
  }
  return (
    <span className="min-[720px]:hidden">
      signed in as <span className="text-ink-soft" translate="no">{user.github_login}</span> ·{" "}
      <button type="button" className="lnk" disabled={logout.isPending} onClick={() => logout.mutate()}>
        {logout.isPending ? "signing out…" : "sign out"}
      </button>
    </span>
  );
}
